import React, { useState } from 'react';
import { motion } from 'framer-motion';
import {
    BrainCircuit,
    Users,
    Zap,
    Rocket,
    BookOpen,
    Briefcase,
    Compass,
    Lightbulb,
    ChevronLeft, // Import for navigation arrows
    ChevronRight, // Import for navigation arrows
} from 'lucide-react';

const App = () => {
    const bannerImage = '/Images/image27.jpeg';
    // State to manage the current slide index for the history section
    const [currentHistorySlide, setCurrentHistorySlide] = useState(0);
    // State to manage the current slide index for the culture section
    const [currentCultureSlide, setCurrentCultureSlide] = useState(0);

    // Data for history cards
    const historyCardsData = [
        { year: "2025 - Founding", description: "Deepbrik is founded by a group of tech enthusiasts with a vision to revolutionize the industry." },
        { year: "2026 - Expansion", description: "Expanded our services and client base, establishing ourselves as a key player in the market." },
        { year: "2027 - Innovation", description: "Launched several innovative products and solutions, garnering industry recognition and awards." },
        { year: "2028 - Global Reach", description: "Expanded our operations globally, serving clients across diverse sectors and geographies." },
        { year: "2029 - Future Vision", description: "Continuously innovating and expanding our reach, we envision a future where technology empowers every individual and business." }, // Added an extra slide for demonstration
    ];

    // Data for culture cards, now with image paths from the public directory
    const cultureCardsData = [
        { icon: BrainCircuit, title: "Innovation", description: "We embrace creativity and are always looking for new and better ways to do things.", imagePath: "/Images/culture/innovation.jpg" },
        { icon: Users, title: "Collaboration", description: "We believe in the power of teamwork and working together to achieve common goals.", imagePath: "/Images/culture/collaboration.jpg" },
        { icon: Zap, title: "Integrity", description: "We act with honesty, transparency, and ethical conduct in all our interactions.", imagePath: "/Images/culture/integrity.jpg" },
        { icon: Rocket, title: "Excellence", description: "We strive for the highest standards of quality and performance in everything we do.", imagePath: "/Images/culture/excellence.jpg" },
        { icon: BookOpen, title: "Learning", description: "We are committed to continuous learning and growth, both individually and as an organization.", imagePath: "/Images/culture/learning.jpg" },
        { icon: Briefcase, title: "Ownership", description: "We take pride in our work and are accountable for our results.", imagePath: "/Images/culture/ownership.jpg" },
        { icon: Compass, title: "Customer Focus", description: "We are dedicated to understanding and meeting the needs of our customers.", imagePath: "/Images/culture/customer-focus.jpg" },
        { icon: Lightbulb, title: "Passion", description: "We are passionate about technology and its potential to change the world.", imagePath: "/Images/culture/passion.jpg" },
    ];

    // Function to go to the next history slide
    const nextHistorySlide = () => {
        setCurrentHistorySlide((prevSlide) =>
            (prevSlide + 1) % historyCardsData.length
        );
    };

    // Function to go to the previous history slide
    const prevHistorySlide = () => {
        setCurrentHistorySlide((prevSlide) =>
            (prevSlide - 1 + historyCardsData.length) % historyCardsData.length
        );
    };

    // Function to go to a specific history slide
    const goToHistorySlide = (index) => {
        setCurrentHistorySlide(index);
    };

    // Function to go to the next culture slide
    const nextCultureSlide = () => {
        setCurrentCultureSlide((prevSlide) =>
            (prevSlide + 1) % cultureCardsData.length
        );
    };

    // Function to go to the previous culture slide
    const prevCultureSlide = () => {
        setCurrentCultureSlide((prevSlide) =>
            (prevSlide - 1 + cultureCardsData.length) % cultureCardsData.length
        );
    };

    // Function to go to a specific culture slide
    const goToCultureSlide = (index) => {
        setCurrentCultureSlide(index);
    };

    return (
        <div className="about-us-container">
            <style>{`
            body {
          margin: 0;
          font-family: 'Segoe UI', sans-serif;
        }
                

                /* Main Container */
                .about-us-container {
                    margin: 0;
                    padding: 0;
                    color: black; /* Default text color for light sections */
                    background-color: white;
                    max-width: 100vw;
                    overflow-x: hidden;
                }

                /* Banner Section */
                .banner {
                    height: 350px; /* Slightly taller banner */
                    background-image: url('https://images.unsplash.com/photo-1504384308090-c894fdcc538d?auto=format&fit=crop&w=600&q=80');
                    background-size: cover;
                    background-position: center;
                    background-repeat: no-repeat;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    position: relative;
                    color: white;
                    text-align: center;
                    width: 100%;
                    animation: banner-zoom 15s infinite alternate; /* Subtle zoom animation */
                }

                // @keyframes banner-zoom {
                //     from {
                //         background-size: 110%;
                //     }
                //     to {
                //         background-size: 100%;
                //     }
                // }

                .banner::before {
                    content: "";
                    position: absolute;
                    inset: 0;
                    background-color: rgba(0, 0, 0, 0.6);
                    backdrop-filter: blur(2px); /* Subtle blur for depth */
                }

                .banner-heading {
                    position: relative;
                    z-index: 1;
                    font-size: 3rem; /* Larger heading */
                    font-weight: bold;
                    text-shadow: 0 4px 8px rgba(0, 0, 0, 0.8); /* Stronger shadow */
                    padding: 0 1rem;
                    letter-spacing: 2px; /* Added letter spacing */
                }

                /* General Section Styles */
                .section {
                    padding: 4rem 1rem; /* Increased vertical padding */
                    text-align: center;
                    width: 100%;
                    box-sizing: border-box;
                    position: relative; /* For potential future animations/elements */
                }

                .section h2 {
                    color: black; /* Darker black for better contrast */
                    font-size: 2.5rem; /* Larger section headings */
                    margin-bottom: 1.5rem; /* Increased margin */
                    font-weight: 700;
                    position: relative;
                    display: inline-block; /* To apply underline effect */
                }

                .section h2::after {
                    content: '';
                    position: absolute;
                    left: 50%;
                    transform: translateX(-50%);
                    bottom: -10px;
                    width: 60px;
                    height: 4px;
                    background-color: #0e2147; /* New accent color */
                    border-radius: 2px;
                }

                .section p {
                    font-size: 1.15rem; /* Slightly larger text */
                    color: black; /* Dark gray for general text */
                    line-height: 1.7; /* Increased line height for readability */
                    margin-bottom: 2rem; /* Increased margin below paragraphs */
                    max-width: 800px;
                    margin-left: auto;
                    margin-right: auto;
                    padding: 0 1rem; /* Ensure padding on smaller screens */
                }

                /* Dark Theme Section */
                .section.dark {
                    background-color: #0e2147; /* New dark background */
                    color: white; /* Lighter text on dark background */
                }

                .section.dark h2 {
                    color: white; /* White heading on dark background */
                }

                .section.dark p {
                    color: white; /* White text on dark background */
                }

                /* Know More Button (General Style) */
                .know-more-button {
                    background-color: #0e2147; /* New accent color */
                    color: white;
                    padding: 0.8rem 2rem;
                    border: none;
                    border-radius: 0.5rem;
                    font-size: 1rem;
                    font-weight: 600;
                    cursor: pointer;
                    transition: background-color 0.3s ease, transform 0.2s ease, box-shadow 0.3s ease;
                    text-decoration: none; /* For anchor tags */
                    display: inline-block; /* For proper padding and alignment */
                    margin-top: 1rem; /* Space above button */
                    box-shadow: 0 4px 10px rgba(14, 33, 71, 0.4); /* Shadow matching new accent color */
                }

                .know-more-button:hover {
                    background-color: black; /* Darker on hover */
                    transform: translateY(-2px);
                    box-shadow: 0 6px 15px rgba(14, 33, 71, 0.6);
                }

                .know-more-button:active {
                    transform: translateY(0);
                    box-shadow: 0 2px 5px rgba(14, 33, 71, 0.3);
                }

                /* Innovative Button Specifics for "Meet Our Team" */
                .meet-team-button {
                    position: relative;
                    z-index: 1;
                    overflow: hidden;
                    background: linear-gradient(90deg, black 0%, #0e2147 100%); /* Black to new accent color gradient */
                    box-shadow: 0 8px 20px rgba(0, 0, 0, 0.4); /* Shadow matching gradient start */
                    animation: pulse-gradient 4s infinite ease-in-out; /* Pulsating gradient animation */
                    background-size: 200% 100%; /* For background-position animation */
                    transition: all 0.4s ease; /* Smooth transitions for all properties */
                }

                @keyframes pulse-gradient {
                    0% { background-position: 0% 50%; }
                    50% { background-position: 100% 50%; }
                    100% { background-position: 0% 50%; }
                }

                .meet-team-button::before {
                    content: '';
                    position: absolute;
                    top: -50%;
                    left: -50%;
                    width: 200%;
                    height: 200%;
                    background: radial-gradient(circle at center, rgba(255, 255, 255, 0.2) 0%, transparent 70%); /* White shimmer effect remains */
                    transform: rotate(45deg);
                    opacity: 0;
                    transition: opacity 0.3s ease;
                }

                .meet-team-button:hover::before {
                    opacity: 1;
                    animation: shimmer 0.8s forwards; /* Shimmer animation on hover */
                }

                @keyframes shimmer {
                    0% { transform: rotate(45deg) translate(-50%, -50%); opacity: 0; }
                    50% { opacity: 0.5; }
                    100% { transform: rotate(45deg) translate(50%, 50%); opacity: 0; }
                }

                .meet-team-button:hover {
                    background: linear-gradient(90deg, #0e2147 0%, black 100%); /* Reverse gradient on hover */
                    transform: translateY(-4px) scale(1.02); /* More pronounced lift and slight scale */
                    box-shadow: 0 12px 30px rgba(14, 33, 71, 0.6); /* Stronger shadow matching new accent color */
                }

                .meet-team-button:active {
                    transform: translateY(0);
                    box-shadow: 0 4px 10px rgba(14, 33, 71, 0.4);
                }


/* Team Section */
.team-container {
    display: flex;
    justify-content: center;
    flex-wrap: wrap;
    gap: 1.5rem;
    padding: 1rem;
    width: 100%;
    box-sizing: border-box;
}

.team-card {
    background-color: white;
    border-radius: 0.75rem;
    box-shadow: 0 0.25rem 0.5rem rgba(0, 0, 0, 0.08);
    width: 160px; /* reduced from 200px */
    flex-shrink: 0;
    transition: transform 0.3s ease, box-shadow 0.3s ease;
    margin: 0.5rem;
    box-sizing: border-box;
    display: flex;
    flex-direction: column;
    align-items: center;
    overflow: hidden;
    padding-bottom: 1rem; /* reduced */
    position: relative;
}

.team-card::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    border-radius: 0.75rem;
    border: 2px solid transparent;
    background: linear-gradient(45deg, #0e2147, black) border-box;
    -webkit-mask:
        linear-gradient(#fff 0 0) padding-box,
        linear-gradient(#fff 0 0);
    -webkit-mask-composite: xor;
    mask-composite: exclude;
    transition: all 0.3s ease;
    opacity: 0;
}

.team-card:hover::before {
    opacity: 1;
}

.team-card:hover {
    transform: translateY(-4px);
    box-shadow: 0 10px 20px rgba(0, 0, 0, 0.15);
}

.team-card img {
    width: 60px; /* reduced from 90px */
    height: 60px;
    border-radius: 50%;
    object-fit: cover;
    margin: 1rem auto 0.6rem; /* reduced margin */
    display: block;
    background-color: white;
    border: 1px solid black;
    transition: transform 0.3s ease;
}

.team-card:hover img {
    transform: scale(1.05);
}

.team-card h3 {
    color: black;
    font-size: 0.95rem; /* reduced */
    margin-bottom: 0.2rem;
    font-weight: 600;
    text-align: center;
}

.team-card p {
    color: black;
    font-size: 0.8rem; /* reduced */
    line-height: 1.3;
    text-align: center;
    padding: 0 0.8rem;
    word-wrap: break-word;
}


                /* Culture Value Section */
.culture-value-icon {
    width: 3.5rem;
    height: 3.5rem;
    margin-bottom: 1.2rem;
    color: white;
    transition: transform 0.3s ease, color 0.3s ease;
    
    /* Center the icon */
    display: block;
    margin-left: auto;
    margin-right: auto;
}


                .culture-value:hover .culture-value-icon {
                    transform: translateY(-5px) rotate(5deg); /* Lift and slight rotate on hover */
                    color: white; /* Icon color changes on hover */
                }

                .culture-value {
                    background-color: white;
                    border-radius: 1rem;
                    padding: 2rem; /* Increased padding */
                    border: 1px solid #ddd; /* Lighter border */
                    box-shadow: 0 0.5rem 1.5rem rgba(0, 0, 0, 0.08); /* More pronounced shadow */
                    width: 100%;
                    max-width: 450px; /* Slightly wider cards */
                    transition: transform 0.3s ease, box-shadow 0.3s ease;
                    text-align: left;
                    display: flex;
                    flex-direction: column;
                    margin: 0 auto 2rem auto; /* Increased bottom margin */
                    box-sizing: border-box;
                    min-height: 250px; /* Increased min-height */
                    position: relative;
                    overflow: hidden;
                    z-index: 1; /* For gradient background */
                    background-size: cover; /* Ensure background image covers */
                    background-position: center; /* Center background image */
                    background-repeat: no-repeat; /* Prevent background image repetition */
                }

                /* Overlay for text readability on top of background images */
                .culture-value::before {
                    content: '';
                    position: absolute;
                    top: 0;
                    left: 0;
                    width: 100%;
                    height: 100%;
                    /* Darker overlay for better contrast with white text, using the new color scheme */
                    background: linear-gradient(to bottom, rgba(0,0,0,0.7) 0%, rgba(14,33,71,0.9) 100%);
                    border-radius: 1rem;
                    transition: opacity 0.4s ease; /* Slower transition for a smoother reveal */
                    opacity: 1; /* Always visible */
                    z-index: -1;
                }

                .culture-value:hover::before {
                    opacity: 0.1; /* Almost transparent on hover to reveal image */
                }

                .culture-value h3 {
                    font-size: 1.45rem; /* Larger font size */
                    font-weight: 600;
                    margin-bottom: 0.8rem;
                    color: white; /* Ensure text is white for contrast with dark overlay */
                    position: relative; /* Bring text above overlay */
                    z-index: 2;
                    transition: color 0.3s ease; /* Smooth color transition */
                }

                .culture-value:hover h3 {
                    color: black; /* Text color changes to black on hover */
                }

                .culture-value p {
                    color: white; /* Ensure text is white for contrast with dark overlay */
                    font-size: 1.05rem;
                    line-height: 1.7;
                    flex-grow: 1;
                    padding: 0; /* Remove internal padding from culture-value p */
                    position: relative; /* Bring text above overlay */
                    z-index: 2;
                    transition: color 0.3s ease; /* Smooth color transition */
                }

                .culture-value:hover p {
                    color: black; /* Text color changes to black on hover */
                }

                /* History Slideshow Styles */
                .history-slideshow {
                    max-width: 800px; /* Limit slideshow width */
                    margin: 2rem auto;
                    position: relative;
                    overflow: hidden; /* Hide overflowing slides */
                    border-radius: 1rem;
                    box-shadow: 0 10px 30px rgba(0, 0, 0, 0.2);
                }

                .history-slideshow-inner {
                    display: flex;
                    transition: transform 0.7s cubic-bezier(0.25, 0.46, 0.45, 0.94); /* Smooth transition for slides */
                }

                .history-slideshow-inner .history-card {
                    flex-shrink: 0; /* Prevent cards from shrinking */
                    width: 100%; /* Each card takes full width of the container */
                    max-width: 800px; /* Ensure card matches slideshow width */
                    margin: 0; /* Remove individual card margins */
                    padding: 2.5rem; /* Increased padding for slides */
                    box-sizing: border-box;
                    background-color: white; /* New dark background for history cards */
                    color: black; /* Lighter text color */
                    border-left: none; /* Remove accent border for slideshow */
                    text-align: center; /* Center content in slide */
                }

                .history-slideshow-inner .history-card h3 {
                    font-size: 1.8rem; /* Larger heading in slideshow */
                    margin-bottom: 1rem;
                    color: black; /* White heading */
                }

                .history-slideshow-inner .history-card p {
                    font-size: 1.1rem; /* Larger paragraph in slideshow */
                    line-height: 1.7;
                    color: black; /* White paragraph */
                }

                /* Culture Slideshow Styles */
                .culture-slideshow {
                    max-width: 900px; /* Slightly wider for culture cards */
                    margin: 2rem auto;
                    position: relative;
                    overflow: hidden;
                    border-radius: 1rem;
                    box-shadow: 0 10px 30px rgba(0, 0, 0, 0.2);
                }

                .culture-slideshow-inner {
                    display: flex;
                    transition: transform 0.7s cubic-bezier(0.25, 0.46, 0.45, 0.94);
                }

                .culture-slideshow-inner .culture-value {
                    flex-shrink: 0;
                    width: 100%;
                    max-width: 900px; /* Ensure card matches slideshow width */
                    margin: 0;
                    padding: 2.5rem; /* Consistent padding with history slideshow */
                    box-sizing: border-box;
                    text-align: center; /* Center content in slide */
                }

                /* Slideshow Navigation Buttons (General) */
                .slideshow-nav-button {
                    position: absolute;
                    top: 50%;
                    transform: translateY(-50%);
                    background-color: rgba(14, 33, 71, 0.8); /* New accent color with transparency */
                    color: white;
                    border: none;
                    border-radius: 50%;
                    width: 45px;
                    height: 45px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    cursor: pointer;
                    font-size: 1.5rem;
                    z-index: 10;
                    transition: background-color 0.3s ease, transform 0.2s ease;
                }

                .slideshow-nav-button:hover {
                    background-color: #0e2147; /* New accent color */
                    transform: translateY(-50%) scale(1.05);
                }

                .slideshow-nav-button.left {
                    left: 10px;
                }

                .slideshow-nav-button.right {
                    right: 10px;
                }

                /* Slideshow Dots Navigation (General) */
                .slideshow-dots {
                    display: flex;
                    justify-content: center;
                    margin-top: 1.5rem;
                    gap: 0.75rem;
                }

                .dot {
                    width: 12px;
                    height: 12px;
                    background-color: rgba(0, 0, 0, 0.4); /* Darker dot for dark section */
                    border-radius: 50%;
                    cursor: pointer;
                    transition: background-color 0.3s ease, transform 0.2s ease;
                }

                .dot.active {
                    background-color: white; /* Active dot color */
                    transform: scale(1.2);
                }

                .dot:hover {
                    background-color: #0e2147;
                    transform: scale(1.1);
                }


                /* Responsive Adjustments */
                @media (max-width: 768px) {
                    .banner {
                        height: 250px;
                    }
                    .banner-heading {
                        font-size: 2.2rem;
                    }

                    .section {
                        padding: 3rem 1rem;
                    }

                    .section h2 {
                        font-size: 2rem;
                    }
                    .section h2::after {
                        bottom: -8px;
                        width: 50px;
                        height: 3px;
                    }

                    .section p {
                        font-size: 1rem;
                        margin-bottom: 1.5rem;
                    }

                    .know-more-button, .meet-team-button {
                        padding: 0.7rem 1.8rem; /* Adjusted for smaller screens */
                        font-size: 0.95rem;
                    }

                    // .team-container {
                    //     flex-direction: column;
                    //     align-items: center;
                    //     gap: 1.5rem;
                    // }

                    // .team-card {
                    //     width: 90%;
                    //     max-width: 280px;
                    // }
                    // .team-card img {
                    //     width: 80px;
                    //     height: 80px;
                    // }

                    .culture-value {
                        padding: 1.5rem;
                        min-height: auto;
                    }
                    .culture-value-icon {
                        width: 3rem;
                        height: 3rem;
                    }
                    .culture-value h3 {
                        font-size: 1.25rem;
                    }
                    .culture-value p {
                        font-size: 0.95rem;
                    }

                    .card-grid { /* This is no longer used for culture cards, but kept for reference if needed elsewhere */
                        grid-template-columns: 1fr;
                        gap: 1.5rem;
                    }
                    .history-card { /* This now applies to the slideshow cards */
                        padding: 1.5rem;
                    }

                    .slideshow-nav-button {
                        width: 35px;
                        height: 35px;
                        font-size: 1.2rem;
                    }

                    /* Responsive adjustments for welcome section */
                    .welcome-section {
                        flex-direction: column; /* Stack image and content vertically */
                        padding: 3rem 1rem;
                    }

                    .welcome-image-container {
                        max-width: 80%; /* Adjust image width for mobile */
                        margin-bottom: 2rem; /* Space below image */
                    }

                    .welcome-content {
                        max-width: 100%; /* Content takes full width */
                        text-align: center; /* Center text on mobile */
                    }

                    .welcome-content h2, .welcome-content p {
                        text-align: center; /* Ensure text is centered on mobile */
                    }

                    .welcome-content h2::after {
                        left: 50%; /* Center underline on mobile */
                        transform: translateX(-50%);
                    }
                }

                @media (max-width: 480px) {
                    .banner-heading {
                        font-size: 1.8rem;
                        letter-spacing: 1px;
                    }
                    .section h2 {
                        font-size: 1.7rem;
                    }
                    .section p {
                        font-size: 0.95rem;
                    }
                    .know-more-button, .meet-team-button {
                        padding: 0.6rem 1.2rem;
                        font-size: 0.85rem;
                    }
                }

                /* New AboutSection styles - integrated and color-adjusted */
                .about-section {
                    display: flex;
                    justify-content: center;
                    align-items: center;
                    padding: 4rem 2rem;
                    gap: 3rem;
                    background: white; /* Changed from #f8fbfd */
                    background-image: url("https://www.transparenttextures.com/patterns/circles.png"); /* Kept texture */
                    background-size: contain;
                    background-repeat: repeat;
                    flex-wrap: wrap;
                }

                .about-left {
                    flex: 1;
                    min-width: 320px;
                    position: relative;
                }

                .about-image-wrapper {
                    position: relative;
                    border-radius: 12px;
                    overflow: hidden;
                }

                .about-image {
                    width: 100%;
                    height: 400px;
                    background-image: url("https://images.unsplash.com/photo-1551836022-d5d88e9218df?auto=format&fit=crop&w=800&q=80");
                    background-size: cover;
                    background-position: center;
                    border-radius: 12px;
                }

                .experience-box {
                    position: absolute;
                    bottom: -20px;
                    left: 20px;
                    background: #0e2147; /* Changed from #001021 */
                    color: white;
                    padding: 2rem;
                    border-radius: 10px;
                    max-width: 250px;
                    box-shadow: 0 10px 30px rgba(0, 0, 0, 0.2);
                }

                .experience-box h2 {
                    font-size: 2.5rem;
                    margin: 0;
                    color: white; /* Ensure text is white */
                }

                .experience-box h4 {
                    margin-top: 0.5rem;
                    margin-bottom: 1rem;
                    font-size: 1.1rem;
                    color: white; /* Ensure text is white */
                }

                .experience-box p {
                    font-size: 0.95rem;
                    line-height: 1.4;
                    color: white; /* Ensure text is white */
                }

                .about-right {
                    flex: 1.2;
                    min-width: 320px;
                }

                .about-tag {
                    color: #0e2147; /* Changed from #007bff */
                    font-weight: bold;
                    margin-bottom: 1rem;
                    border-bottom: 2px solid #0e2147; /* Changed from #007bff */
                    display: inline-block;
                }

                .about-right h1 {
                    font-size: 2rem;
                    color: black; /* Changed from #001021 */
                    margin-bottom: 1rem;
                    font-weight: 700;
                }

                .about-right p {
                    font-size: 1.05rem;
                    color: black; /* Changed from #333 */
                    line-height: 1.7;
                    margin-bottom: 1.5rem;
                }

                .about-button {
                    background: #0e2147; /* Changed from #0f1626 */
                    color: white;
                    padding: 0.75rem 1.5rem;
                    border: none;
                    border-radius: 30px;
                    font-weight: bold;
                    cursor: pointer;
                    transition: 0.3s ease;
                }

                .about-button:hover {
                    background: black; /* Changed from #1c2a3b */
                }

                @media (max-width: 768px) {
                    .about-section {
                        flex-direction: column;
                        text-align: center;
                    }

                    .experience-box {
                        left: 50%;
                        transform: translateX(-50%);
                    }

                    .about-right h1 {
                        font-size: 1.5rem;
                    }

                    .about-right p {
                        font-size: 1rem;
                    }
                }
                    .contact-section{
    flex-direction: column;
  }
}
.contact-section {
  background: white ;
  padding: 80px 20px;
  color: white;
}

.contact-container {
  max-width: 600px;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  gap: 40px;
  padding: 40px;
  border-radius: 20px;
  background: #0e2147;
  backdrop-filter: blur(12px);
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.4);
}

.contact-info h2 {
  font-size: 42px;
  margin-bottom: 20px;
  color: white;
}

.contact-info p {
  font-size: 18px;
  line-height: 1.7;
  color: white;
}

.contact-form {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.form-row {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.form-row input {
  flex: 1;
}

.contact-form input,
.contact-form textarea {
  padding: 16px;
  border: none;
  border-radius: 12px;
  font-size: 1rem;
  color: #333;
  background: #fff;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
  transition: all 0.3s ease;
}

.contact-form input:focus,
.contact-form textarea:focus {
  outline: none;
  box-shadow: 0 0 0 3px rgba(75, 0, 179, 0.4);
}

.contact-form textarea {
  min-height: 140px;
  resize: vertical;
}

.contact-form button {
            background-color: transparent;
            color: white;
            border: 1px solid white;
            padding: 0.8rem 1.5rem;
            font-size: 1rem;
            cursor: pointer;
            border-radius: 5px;
            transition: 0.3s ease;
          }

          .contact-form button:hover {
            background-color: white;
            color: white;
          }


/* Responsive */
@media (min-width: 768px) {
  .contact-container {
    flex-direction: row;
    justify-content: space-between;
    align-items: flex-start;
  }

  .contact-info {
    flex: 1;
    padding-right: 40px;
  }

  .contact-form {
    flex: 1;
  }

  .form-row {
    flex-direction: row;
    gap: 20px;
  }
}
            `}</style>

            {/* Banner Section */}
            <section className="banner">
                <div
        className="contact-banner"
        style={{ backgroundImage: `url(${bannerImage})` }} // Set background image dynamically
      ></div>
                <motion.h1
                    className="banner-heading"
                    initial={{ opacity: 0, y: -50 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8, delay: 0.2 }}
                >
                    About Deepbrik
                </motion.h1>
            </section>

            {/* New About Section */}
            <section className="about-section">
                <div className="about-left">
                    <div className="about-image-wrapper">
                        <div className="about-image" />
                        <div className="experience-box">
                            <h2>2+</h2>
                            <h4>Years of Experience</h4>
                            <p>
                                The trusted choice for your <br />
                                software development <br />
                                solutions
                            </p>
                        </div>
                    </div>
                </div>
                <div className="about-right">
                    <div className="about-tag">About Company</div>
                    <h1>
                        Our Vision: Empowering Global <br />
                        Businesses with Intelligent Technology
                    </h1>
                    <p>
                        At Deepbrik, we are committed to delivering innovative AI, Machine
                        Learning, and Blockchain solutions that help businesses adapt and
                        excel in an increasingly complex and competitive marketplace.
                    </p>
                    <button className="about-button">Know More About Us</button>
                </div>
            </section>

            {/* History Section - Now a Slideshow */}
            <section className="section dark" id="our-history">
                <motion.h2
                    initial={{ opacity: 0, y: 50 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.6 }}
                >
                    Our History
                </motion.h2>
                <motion.p
                    initial={{ opacity: 0, y: 50 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.6, delay: 0.2 }}
                >
                    Explore the key milestones and achievements that have shaped Deepbrik into the innovative company it is today.
                </motion.p>

                <div className="history-slideshow">
                    <div
                        className="history-slideshow-inner"
                        style={{ transform: `translateX(-${currentHistorySlide * 100}%)` }}
                    >
                        {historyCardsData.map((card, index) => (
                            <motion.div
                                key={index}
                                className="history-card"
                                // Framer motion animations for individual slides when they come into view
                                initial={{ opacity: 0, y: 50 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                transition={{ duration: 0.5, delay: 0.1 }}
                            >
                                <h3>{card.year}</h3>
                                <p>{card.description}</p>
                            </motion.div>
                        ))}
                    </div>

                    {/* Slideshow Navigation Buttons */}
                    <button className="slideshow-nav-button left" onClick={prevHistorySlide}>
                        <ChevronLeft size={24} />
                    </button>
                    <button className="slideshow-nav-button right" onClick={nextHistorySlide}>
                        <ChevronRight size={24} />
                    </button>
                </div>

                {/* Slideshow Dots Navigation */}
                <div className="slideshow-dots">
                    {historyCardsData.map((_, index) => (
                        <span
                            key={index}
                            className={`dot ${index === currentHistorySlide ? 'active' : ''}`}
                            onClick={() => goToHistorySlide(index)}
                        ></span>
                    ))}
                </div>

                <motion.a
                    href="#our-team"
                    className="know-more-button meet-team-button"
                    initial={{ opacity: 0, scale: 0.8 }}
                    whileInView={{ opacity: 1, scale: 1 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5, delay: 0.5 }}
                >
                    Meet Our Team
                </motion.a>
            </section>

            {/* Our Team Section */}
            <section className="section" id="our-team">
                <motion.h2
                    initial={{ opacity: 0, y: 50 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.6 }}
                >
                    Our Team
                </motion.h2>
                <motion.p
                    initial={{ opacity: 0, y: 50 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.6, delay: 0.2 }}
                >
                </motion.p>
                <div className="team-container">
                    {[
                        { src: "/Images/ceo.png", name: "Lavakumar N", title: "Founder & MD" },
                        { src: "/Images/director2.png", name: "Manjunath N", title: "Director" },
                        // { src: "/Images/CEO2.png", name: "Aruna Kumari K", title: "CEO" },
                        { src: "/Images/VP.png", name: "Sai Sreenivas S", title: "VP & Head" },
                        { src: "/Images/CTO2.png", name: "Swetha B", title: "CTO" },
                        { src: "/Images/cao.png", name: "ArunKumar K", title: "CAO" },
                    ].map((member, index) => (
                        <motion.div
                            key={index}
                            className="team-card"
                            initial={{ opacity: 0, scale: 0.8 }}
                            whileInView={{ opacity: 1, scale: 1 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.5, delay: index * 0.1 }}
                        >
                            <img src={member.src} alt={member.name} />
                            <h3>{member.name}</h3>
                            <p>{member.title}</p>
                        </motion.div>
                    ))}
                </div>
                <motion.a
                    href="#our-culture"
                    className="know-more-button"
                    initial={{ opacity: 0, scale: 0.8 }}
                    whileInView={{ opacity: 1, scale: 1 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5, delay: 0.6 }}
                >
                    Discover Our Values
                </motion.a>
            </section>

            {/* Our Culture Section - Now a Slideshow */}
            <section className="section dark" id="our-culture">
                <motion.h2
                    initial={{ opacity: 0, y: 50 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.6 }}
                >
                    Our Culture
                </motion.h2>
                <motion.p
                    initial={{ opacity: 0, y: 50 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.6, delay: 0.2 }}
                >
                    At **Deepbrik**, we believe that our people are our greatest asset. We have cultivated a culture
                    that celebrates diversity, fosters innovation, and promotes collaboration. Our core values
                    shape everything we do, driving us to pursue excellence, act with integrity, and make a positive
                    impact on the world.
                </motion.p>
                <div className="culture-slideshow">
                    <div
                        className="culture-slideshow-inner"
                        style={{ transform: `translateX(-${currentCultureSlide * 100}%)` }}
                    >
                        {cultureCardsData.map((value, index) => (
                            <motion.div
                                key={index}
                                className="culture-value" // Removed dynamic class, now directly using inline style for background-image
                                style={{ backgroundImage: `url(${value.imagePath})` }} // Set background image dynamically
                                initial={{ opacity: 0, scale: 0.9 }}
                                whileInView={{ opacity: 1, scale: 1 }}
                                viewport={{ once: true }}
                                transition={{ duration: 0.5, delay: index * 0.1 }}
                            >
                                <value.icon className="culture-value-icon" />
                                <h3>{value.title}</h3>
                                <p>{value.description}</p>
                            </motion.div>
                        ))}
                    </div>

                    {/* Slideshow Navigation Buttons for Culture */}
                    <button className="slideshow-nav-button left" onClick={prevCultureSlide}>
                        <ChevronLeft size={24} />
                    </button>
                    <button className="slideshow-nav-button right" onClick={nextCultureSlide}>
                        <ChevronRight size={24} />
                    </button>
                </div>

                {/* Slideshow Dots Navigation for Culture */}
                <div className="slideshow-dots">
                    {cultureCardsData.map((_, index) => (
                        <span
                            key={index}
                            className={`dot ${index === currentCultureSlide ? 'active' : ''}`}
                            onClick={() => goToCultureSlide(index)}
                        ></span>
                    ))}
                </div>
            </section>
            
      <section className="contact-section">
        <div className="contact-container">
          <h2>Contact Us</h2>
          <form className="contact-form">
            <input type="text" placeholder="Your Name" required />
            <input type="email" placeholder="Your Email" required />
            <textarea placeholder="Your Message" required></textarea>
            <button type="submit">SEND MESSAGE</button>
          </form>
        </div>
      </section>
        </div>
    );
};

export default App;
